import type { ReactNode } from 'react';
import React, { useRef, useState } from 'react';
import ArrowRightIcon from '@assets/icons/arrow-right.svg';
import InfoCircleIcon from '@assets/icons/info-circle.svg';
import UploadIcon from '@assets/icons/profile/upload.svg';
import { BaseAlertBanner, BaseButton, BaseDragAndDrop, BaseModal, BaseSpin, UploadProgressBar } from '@components';
import {
    SHARED_IMAGE_UPLOAD_MODAL_CANCEL,
    SHARED_IMAGE_UPLOAD_MODAL_CURRENT_LABEL,
    SHARED_IMAGE_UPLOAD_MODAL_DRAG_AND_DROP,
    SHARED_IMAGE_UPLOAD_MODAL_NEW_LABEL,
} from '@components/image-upload-modal/constants.ts';
import { PhotoUploadFailure } from '@components/image-upload-modal/PhotoUploadFailure.tsx';
import { PhotoUploadSuccess } from '@components/image-upload-modal/PhotoUploadSuccess.tsx';
import {
    ContentWrapper,
    ImageLabel,
    PhotosWrapper,
    Title,
    UploadAreaFile,
    UploadAreaHintText,
} from '@components/image-upload-modal/styled.tsx';
import CloseIcon from '@components/modal/close-icon';
import type { UploadWithProgressPayload } from '@components/upload-progress-bar/types.ts';
import { TEST_IDS } from '@constants/testIds.ts';
import { useLingui } from '@lingui/react/macro';
import { MODAL_FORCED_STYLES } from '@routes/___shared___/styled.tsx';
import { ModalFooterWrapper as FooterWrapper } from '@routes/___shared___/styled.tsx';
import type { UploadFileInfo } from '@types';
import { getFileSizeInMB, isFileSizeValid } from '@utils/file.ts';
import { translateItem } from '@utils/translations.ts';
import { Form } from 'antd';

interface Props {
    open: boolean;
    currentImageUrl: string;
    newImageUrl: string;
    texts: {
        uploadFileInfo: string;
        guidelinesTitle: string;
        successMessage: string;
        failureMessage: string;
        failureDescription: string;
        title: string;
    };
    currentPlaceholder: ReactNode;
    newPlaceholder: ReactNode;
    imageInfoWrapper: React.ComponentType<{ children: ReactNode }>;
    guidelines: ReactNode;
    formDataKey: string;
    isFileTypeValid: (type: string) => boolean;
    onUpload: (params: UploadWithProgressPayload) => Promise<unknown> | unknown;
    onClose: () => void;
    onSubmit: () => void;
}

export const IMAGE_UPLOAD_MAX_SIZE_MB = 5;
const UPLOAD_PROGRESS_INITIAL_STATE = {
    isOpened: false,
    filename: '',
    sizeInMB: 0,
    timeInSeconds: 0,
    percentageProgress: 0,
    startedAt: 0,
};

export const ImageUploadModal: React.FC<Props> = ({
    open,
    currentImageUrl,
    newImageUrl,
    texts,
    currentPlaceholder,
    newPlaceholder,
    imageInfoWrapper: ImageInfoWrapper,
    guidelines,
    formDataKey,
    isFileTypeValid,
    onUpload,
    onClose,
    onSubmit,
}) => {
    const { t } = useLingui();
    const abortControllerRef = useRef<AbortController | null>(null);

    const [isUploaded, setIsUploaded] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [fileInfo, setFileInfo] = useState<UploadFileInfo | null>(null);
    const [uploadProgress, setUploadProgress] = useState(UPLOAD_PROGRESS_INITIAL_STATE);

    const clearProgress = () => setUploadProgress(UPLOAD_PROGRESS_INITIAL_STATE);

    const resetAndClose = () => {
        setIsUploaded(false);
        setIsSuccessful(false);
        setIsUploading(false);
        setFileInfo(null);
        onClose();
    };

    const handleSubmit = () => {
        onSubmit();
        setIsUploaded(false);
        setIsSuccessful(false);
    };

    const submitButton = (
        <Form.Item noStyle>
            <BaseButton
                disabled={!isUploaded || !isSuccessful}
                onClick={handleSubmit}
                data-cy={TEST_IDS.SHARED.IMAGE_UPLOAD_SUBMIT}
            >
                {t`Upload`}
            </BaseButton>
        </Form.Item>
    );

    const cancelButton = (
        <BaseButton $customType="outline" onClick={resetAndClose}>
            {translateItem(SHARED_IMAGE_UPLOAD_MODAL_CANCEL)}
        </BaseButton>
    );

    return (
        <BaseModal
            styles={MODAL_FORCED_STYLES}
            title={<Title>{texts.title}</Title>}
            open={open}
            onClose={resetAndClose}
            closeIcon={<CloseIcon onClick={resetAndClose} />}
            footer={
                <FooterWrapper>
                    {cancelButton}
                    {submitButton}
                </FooterWrapper>
            }
            content={
                <ContentWrapper data-cy={TEST_IDS.SHARED.IMAGE_UPLOAD_CONTENT}>
                    <PhotosWrapper>
                        <ImageInfoWrapper>
                            <ImageLabel>{translateItem(SHARED_IMAGE_UPLOAD_MODAL_CURRENT_LABEL)}</ImageLabel>
                            {currentImageUrl ? (
                                <img src={currentImageUrl} alt={t`current image`} />
                            ) : (
                                currentPlaceholder
                            )}
                        </ImageInfoWrapper>

                        <ArrowRightIcon />

                        <ImageInfoWrapper>
                            <ImageLabel>{translateItem(SHARED_IMAGE_UPLOAD_MODAL_NEW_LABEL)}</ImageLabel>
                            {isUploaded && isSuccessful && newImageUrl ? (
                                <img src={newImageUrl} alt={t`new image`} />
                            ) : (
                                newPlaceholder
                            )}
                        </ImageInfoWrapper>
                    </PhotosWrapper>

                    <BaseDragAndDrop
                        showUploadList={false}
                        content={
                            <>
                                <UploadIcon />
                                <UploadAreaHintText>
                                    {translateItem(SHARED_IMAGE_UPLOAD_MODAL_DRAG_AND_DROP)}
                                </UploadAreaHintText>
                                <UploadAreaFile>{texts.uploadFileInfo}</UploadAreaFile>
                            </>
                        }
                        customRequest={async ({ file, onError, onSuccess }) => {
                            const typedFile = file as File;
                            const isTypeValid = isFileTypeValid(typedFile.type || '');
                            const isSizeValid = isFileSizeValid(typedFile.size || 0, IMAGE_UPLOAD_MAX_SIZE_MB);

                            if (!isTypeValid || !isSizeValid) {
                                onError?.(new Error('Invalid file'));
                                return;
                            }

                            const controller = new AbortController();
                            abortControllerRef.current = controller;

                            const formData = new FormData();
                            formData.append(formDataKey, file);

                            setUploadProgress({
                                isOpened: true,
                                filename: typedFile.name,
                                sizeInMB: Number(getFileSizeInMB(typedFile.size)),
                                timeInSeconds: 0,
                                percentageProgress: 0,
                                startedAt: Date.now(),
                            });

                            try {
                                await onUpload({
                                    formData,
                                    signal: controller.signal,
                                    onUploadProgress: (progress) => {
                                        setUploadProgress((prev) => ({
                                            ...prev,
                                            percentageProgress: progress,
                                        }));
                                    },
                                });

                                onSuccess?.(file);
                            } catch (err) {
                                onError?.(err as Error);
                            }
                        }}
                        onChange={(info) => {
                            const { size = 0, status, type = '' } = info.file;

                            setFileInfo(info);
                            setIsUploading(status === 'uploading');

                            if (status === 'uploading') {
                                setIsSuccessful(isFileTypeValid(type));
                            }

                            if (status === 'done' || status === 'error') {
                                setIsUploaded(true);
                                setIsSuccessful(
                                    status === 'done' &&
                                        isFileTypeValid(type) &&
                                        isFileSizeValid(size, IMAGE_UPLOAD_MAX_SIZE_MB),
                                );
                            }
                        }}
                    />

                    {!!isUploading && <BaseSpin size="small" />}

                    {!!(isUploaded && isSuccessful) && (
                        <PhotoUploadSuccess
                            fileName={fileInfo?.file?.name || ''}
                            fileSize={fileInfo?.file?.size ? getFileSizeInMB(fileInfo.file.size) : ''}
                            message={texts.successMessage}
                        />
                    )}

                    {!!(isUploaded && !isSuccessful) && (
                        <PhotoUploadFailure message={texts.failureMessage} description={texts.failureDescription} />
                    )}

                    <BaseAlertBanner
                        icon={<InfoCircleIcon />}
                        type="info"
                        title={texts.guidelinesTitle}
                        description={guidelines}
                    />

                    <UploadProgressBar
                        {...uploadProgress}
                        minutesLeft={0}
                        onStopUploading={() => {
                            abortControllerRef.current?.abort();
                            clearProgress();
                        }}
                        onCompleted={clearProgress}
                    />
                </ContentWrapper>
            }
        />
    );
};
